/**
 * ServiceDesignerModelBuilder — manage flexible models/templates (spec §3–4,
 * §11, §17.5). Lists existing models and lets an admin create a new one with
 * recommended nodes (role + optional CI type) and relations (role→role).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import './ServiceDesigner.css';

const TYPE_OPTIONS = [
  { value: '', label: '— Cualquier tipo —' },
  { value: 'service', label: 'Servicio' },
  { value: 'rack_element', label: 'Servidor' },
  { value: 'virt_vm', label: 'VM' },
  { value: 'printer', label: 'Impresora' },
  { value: 'wifi_controller', label: 'Controladora WiFi' },
  { value: 'wifi_access_point', label: 'AP WiFi' },
  { value: 'firewall', label: 'Firewall' },
  { value: 'router', label: 'Router' },
  { value: 'workstation', label: 'Puesto' },
  { value: 'device', label: 'Dispositivo' },
  { value: 'storage_disk', label: 'Disco' },
  { value: 'isp_circuit', label: 'Circuito ISP' },
  { value: 'ipam_ip', label: 'IP' },
];

let __k = 1;
const nk = () => `k${__k++}`;

export default function ServiceDesignerModelBuilder({ companyId, onClose, onSaved }) {
  const [models, setModels] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nodes, setNodes] = useState([{ key: nk(), role: '', ci_type: '', required: false }]);
  const [relations, setRelations] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, rt] = await Promise.all([
        api.sdListModels(companyId),
        api.sdListRelationTypes(companyId),
      ]);
      setModels(m.data || []);
      setRelationTypes(rt.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const addNode = () => setNodes((p) => [...p, { key: nk(), role: '', ci_type: '', required: false }]);
  const updNode = (key, patch) => setNodes((p) => p.map((n) => (n.key === key ? { ...n, ...patch } : n)));
  const delNode = (key) => setNodes((p) => p.filter((n) => n.key !== key));

  const addRelation = () => setRelations((p) => [...p, { key: nk(), source_role: '', relation_type_id: '', target_role: '', required: false }]);
  const updRel = (key, patch) => setRelations((p) => p.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const delRel = (key) => setRelations((p) => p.filter((r) => r.key !== key));

  const save = async () => {
    if (!name.trim()) { setError('El nombre de la plantilla es obligatorio.'); return; }
    setError(null);
    setSaving(true);
    try {
      const cleanNodes = nodes
        .filter((n) => (n.role || '').trim())
        .map((n, i) => ({ role: n.role.trim(), ci_type: n.ci_type || null, required: !!n.required, sort_order: i }));
      const cleanRels = relations
        .filter((r) => (r.source_role || '').trim() && (r.target_role || '').trim() && r.relation_type_id)
        .map((r) => ({
          source_role: r.source_role.trim(),
          target_role: r.target_role.trim(),
          relation_type_id: Number(r.relation_type_id),
          required: !!r.required,
        }));
      const res = await api.sdCreateModel(companyId, { name: name.trim(), description: description.trim() || null, nodes: cleanNodes, relations: cleanRels });
      if (onSaved) onSaved(res.data);
      setName('');
      setDescription('');
      setNodes([{ key: nk(), role: '', ci_type: '', required: false }]);
      setRelations([]);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal sd-builder" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>📦 Plantillas / Modelos</h3>
          <button className="sd-model-x" onClick={onClose} title="Cerrar"><XMarkIcon width={16} height={16} /></button>
        </div>
        <p className="sd-modal-sub">
          Las plantillas son aceleradores: definen roles y relaciones recomendadas. Nunca limitan el grafo.
        </p>

        {error && <div className="sd-error-banner">{error}<button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button></div>}

        {/* ── Lista de plantillas existentes ── */}
        <div className="sd-builder-section">
          <p className="sd-builder-section-title">Plantillas existentes</p>
          {loading ? (
            <p className="sd-hint">Cargando…</p>
          ) : models.length === 0 ? (
            <div className="sd-empty">Todavía no hay plantillas.</div>
          ) : (
            <div className="sd-builder-list">
              {models.map((m) => (
                <div key={m.id} className="sd-builder-row">
                  <span className="sd-builder-row-name">{m.name}</span>
                  <span className="sd-builder-row-meta">{(m.nodes || []).length} roles · {(m.relations || []).length} relaciones</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Crear plantilla ── */}
        <div className="sd-builder-section">
          <p className="sd-builder-section-title">＋ Nueva plantilla</p>
          <label className="sd-builder-label">Nombre</label>
          <input className="sd-builder-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej.: Aplicación empresarial" />

          <label className="sd-builder-label">Descripción</label>
          <textarea className="sd-builder-input sd-builder-area" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Opcional" rows={2} />

          <div className="sd-builder-subhead">Roles (nodos recomendados)</div>
          {nodes.map((n) => (
            <div key={n.key} className="sd-builder-editrow">
              <input
                className="sd-builder-input sd-builder-role"
                value={n.role}
                onChange={(e) => updNode(n.key, { role: e.target.value })}
                placeholder="Rol (p. ej. Base de datos)"
              />
              <select className="sd-builder-input sd-builder-type" value={n.ci_type} onChange={(e) => updNode(n.key, { ci_type: e.target.value })}>
                {TYPE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
              </select>
              <label className="sd-builder-req" title="Obligatorio al aplicar">
                <input type="checkbox" checked={n.required} onChange={(e) => updNode(n.key, { required: e.target.checked })} /> req
              </label>
              <button className="sd-role-chip-x" onClick={() => delNode(n.key)} title="Quitar rol">✕</button>
            </div>
          ))}
          <button className="sd-builder-add" onClick={addNode}><PlusIcon width={14} height={14} /> Añadir rol</button>

          <div className="sd-builder-subhead">Relaciones recomendadas</div>
          {relations.length === 0 && <p className="sd-hint">Sin relaciones (opcional).</p>}
          {relations.map((r) => (
            <div key={r.key} className="sd-builder-editrow sd-builder-reledit">
              <input className="sd-builder-input sd-builder-role" value={r.source_role} onChange={(e) => updRel(r.key, { source_role: e.target.value })} placeholder="Rol origen" />
              <select className="sd-builder-input sd-builder-rel" value={r.relation_type_id} onChange={(e) => updRel(r.key, { relation_type_id: e.target.value })}>
                <option value="">— relación —</option>
                {relationTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.display_name}</option>)}
              </select>
              <input className="sd-builder-input sd-builder-role" value={r.target_role} onChange={(e) => updRel(r.key, { target_role: e.target.value })} placeholder="Rol destino" />
              <label className="sd-builder-req" title="Obligatorio">
                <input type="checkbox" checked={r.required} onChange={(e) => updRel(r.key, { required: e.target.checked })} /> req
              </label>
              <button className="sd-role-chip-x" onClick={() => delRel(r.key)} title="Quitar relación">✕</button>
            </div>
          ))}
          <button className="sd-builder-add" onClick={addRelation}><PlusIcon width={14} height={14} /> Añadir relación</button>
        </div>

        <div className="sd-modal-actions">
          <button className="sd-cancel-btn" onClick={onClose}>Cerrar</button>
          <button className="sd-save-btn" disabled={saving} onClick={save}>
            {saving ? 'Guardando…' : 'Guardar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
}
